import '../../styles/restScreens.css';
import { useState, useEffect } from 'react';
import { supabaseClient } from '../../config/supabase';
import ClientsView from '../clients/ClientsView';
import PipelineView from '../pipeline/PipelineView';

// Module 2 — the CRM screen: two sections, Clients and Pipeline (UX Addendum §4b).
//
// ENTITLEMENT (blueprint decision #5 · build step 5). The Pipeline section is gated by
// has_capability('crm.pipeline'). Worth being precise about what this gate is and is not:
//
//   * The DATA LAYER is the enforcement. opportunities' RLS carries
//     `AND has_capability('crm.pipeline')`, so a tenant without the key reads ZERO rows and
//     is refused on insert — proven live before this UI existed. Even if someone edited this
//     component in their browser, they would get an empty board and errors on write.
//   * This check is COURTESY. It hides a section the tenant hasn't bought instead of showing
//     them an empty board and letting them wonder what's broken.
//
// That order matters: "enabled" is not "enforced", and the UI is never the thing enforcing.
//
// Super-admins need no special case here — has_capability() already returns true for them.

function CrmScreen({ userId, theme = 'light', isUser = false }) {

    const [section, setSection] = useState('Clients');
    const [hasPipeline, setHasPipeline] = useState(null);   // null = still asking

    useEffect(() => {
        let alive = true;
        (async () => {
            const { data, error } = await supabaseClient.rpc('has_capability', { p_key: 'crm.pipeline' });
            if (!alive) return;
            // Fail closed on error: if we cannot establish the tenant holds the key, assume
            // they don't. The database would refuse the rows anyway; don't offer the door.
            setHasPipeline(error ? false : !!data);
        })();
        return () => { alive = false; };
    }, []);

    // A tenant who loses the capability while sitting on the Pipeline tab gets moved back
    // rather than left staring at a section that will only ever be empty.
    useEffect(() => {
        if (hasPipeline === false && section === 'Pipeline') setSection('Clients');
    }, [hasPipeline, section]);

    const sections = hasPipeline ? ['Clients', 'Pipeline'] : ['Clients'];
    const border = 'border-[var(--surface-border)]';

    return (
        <div data-m6-layout="clients" data-m6-component="CrmScreen">
            {/* Only render the switcher once there's a real choice to make. */}
            {sections.length > 1 && (
                <div className={`inline-flex items-center gap-2 mb-4 ${border}`} role="tablist" aria-label="CRM sections">
                    {sections.map(s => (
                        <button
                            key={s}
                            type="button"
                            role="tab"
                            aria-selected={section === s}
                            onClick={() => setSection(s)}
                            className={`ia-hover-pulse rounded-full border bg-transparent px-4 py-2 text-sm font-medium transition
                                ${section === s
                                    ? 'ia-selected-pill'
                                    : 'border-[var(--ui-border)] bg-transparent text-[var(--ink-muted)] hover:border-[#03B7C9] hover:text-[#03B7C9]'}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            {section === 'Pipeline' && hasPipeline
                ? <PipelineView userId={userId} theme={theme} isUser={isUser} />
                : <ClientsView userId={userId} theme={theme} isUser={isUser} />}
        </div>
    );
}

export default CrmScreen;
